import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';
import { APIResponse } from '@/client/types/api';
import {
  PaymentRefundPayload,
  RefundRequestAction,
  Subscription,
  WalletAdjustPayload,
  WalletPayoutAction,
} from '@/client/types/payment';
import { paymentKeys } from './payment.query';

const B = '/api/admin/v2';
const TIMEOUT = { timeout: 45_000 };

/** ff-api wraps every payload in `{ status, data }` — always read `.data`. */
const unwrap = async <T>(promise: Promise<APIResponse<T> | T>): Promise<T> => {
  const res = await promise;
  if (res != null && typeof res === 'object' && 'data' in (res as object) && 'status' in (res as object)) {
    return (res as APIResponse<T>).data as T;
  }
  return res as T;
};

const invalidatePayments = (qc: ReturnType<typeof useQueryClient>, pid?: number) => {
  qc.invalidateQueries({ queryKey: paymentKeys.payments() });
  if (pid != null) qc.invalidateQueries({ queryKey: paymentKeys.paymentDetail(pid) });
};

const invalidateInvoices = (qc: ReturnType<typeof useQueryClient>, invid?: number) => {
  qc.invalidateQueries({ queryKey: paymentKeys.invoices() });
  if (invid != null) qc.invalidateQueries({ queryKey: paymentKeys.invoiceDetail(invid) });
};

const invalidateTransactions = (qc: ReturnType<typeof useQueryClient>, tid?: number) => {
  qc.invalidateQueries({ queryKey: paymentKeys.transactions() });
  if (tid != null) qc.invalidateQueries({ queryKey: paymentKeys.transactionDetail(tid) });
};

const invalidateSubs = (qc: ReturnType<typeof useQueryClient>, plid?: number) => {
  qc.invalidateQueries({ queryKey: paymentKeys.subs() });
  if (plid != null) qc.invalidateQueries({ queryKey: paymentKeys.subDetail(plid) });
};

const invalidateOrders = (qc: ReturnType<typeof useQueryClient>, orderid?: number) => {
  qc.invalidateQueries({ queryKey: paymentKeys.orders() });
  if (orderid != null) qc.invalidateQueries({ queryKey: paymentKeys.orderDetail(orderid) });
};

// ─── Payments ─────────────────────────────────────────────────────────────────

export const useRefundPaymentMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { pid: number; body?: PaymentRefundPayload; idempotencyKey?: string }) => {
      return unwrap(request<any>(`${B}/payments/${args.pid}/refund`, 'POST', args.body ?? {}, {
        ...TIMEOUT,
        idempotencyKey: args.idempotencyKey || generateIdempotencyKey(),
      }));
    },
    'Failed to refund payment',
  );
  return useMutation({
    mutationFn: (args: { pid: number; body?: PaymentRefundPayload; idempotencyKey?: string }) =>
      fn(args),
    onSuccess: (_d, { pid }) => {
      invalidatePayments(qc, pid);
      qc.invalidateQueries({ queryKey: paymentKeys.invoices() });
    },
  });
};

export const useVerifyPaymentMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { pid: number; fulfill?: boolean }) => {
    return unwrap(request<any>(
      `${B}/payments/${args.pid}/verify`,
      'POST',
      { fulfill: !!args.fulfill },
      TIMEOUT,
    ));
  }, 'Failed to verify payment');
  return useMutation({
    mutationFn: (args: { pid: number; fulfill?: boolean }) => fn(args),
    onSuccess: (_d, { pid }) => invalidatePayments(qc, pid),
  });
};

export const useFulfillPaymentMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (pid: number) => {
    return unwrap(request<any>(`${B}/payments/${pid}/fulfill`, 'POST', {}, TIMEOUT));
  }, 'Failed to fulfill payment');
  return useMutation({
    mutationFn: (pid: number) => fn(pid),
    onSuccess: (_d, pid) => invalidatePayments(qc, pid),
  });
};

export const useCancelPaymentMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (pid: number) => {
    return unwrap(request<any>(`${B}/payments/${pid}/cancel`, 'POST', {}, TIMEOUT));
  }, 'Failed to cancel payment');
  return useMutation({
    mutationFn: (pid: number) => fn(pid),
    onSuccess: (_d, pid) => invalidatePayments(qc, pid),
  });
};

export const useUpdatePaymentMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: {
      pid: number;
      patch: Partial<{
        verified: boolean | number;
        fulfilled: boolean | number;
        canceled: boolean | number;
        refund: boolean | number;
        ref_notes: string;
      }>;
    }) => {
      return unwrap(request<any>(`${B}/payments/${args.pid}`, 'PATCH', args.patch, TIMEOUT));
    },
    'Failed to update payment',
  );
  return useMutation({
    mutationFn: (args: {
      pid: number;
      patch: Partial<{
        verified: boolean | number;
        fulfilled: boolean | number;
        canceled: boolean | number;
        refund: boolean | number;
        ref_notes: string;
      }>;
    }) => fn(args),
    onSuccess: (_d, { pid }) => invalidatePayments(qc, pid),
  });
};

// ─── Invoices ─────────────────────────────────────────────────────────────────

export const useMarkInvoicePaidMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (invid: number) => {
    return unwrap(request<any>(`${B}/invoices/${invid}/mark-paid`, 'POST', {}, TIMEOUT));
  }, 'Failed to mark invoice paid');
  return useMutation({
    mutationFn: (invid: number) => fn(invid),
    onSuccess: (_d, invid) => invalidateInvoices(qc, invid),
  });
};

export const useFinalizeInvoiceMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (invid: number) => {
    return unwrap(request<any>(`${B}/invoices/${invid}/finalize`, 'POST', {}, TIMEOUT));
  }, 'Failed to finalize invoice');
  return useMutation({
    mutationFn: (invid: number) => fn(invid),
    onSuccess: (_d, invid) => invalidateInvoices(qc, invid),
  });
};

export const useVoidInvoiceMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { invid: number; note?: string }) => {
    return unwrap(request<any>(
      `${B}/invoices/${args.invid}/void`,
      'POST',
      args.note ? { note: args.note } : {},
      TIMEOUT,
    ));
  }, 'Failed to void invoice');
  return useMutation({
    mutationFn: (args: { invid: number; note?: string }) => fn(args),
    onSuccess: (_d, { invid }) => invalidateInvoices(qc, invid),
  });
};

export const useCancelInvoiceMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { invid: number; note?: string }) => {
    return unwrap(request<any>(
      `${B}/invoices/${args.invid}/cancel`,
      'POST',
      args.note ? { note: args.note } : {},
      TIMEOUT,
    ));
  }, 'Failed to cancel invoice');
  return useMutation({
    mutationFn: (args: { invid: number; note?: string }) => fn(args),
    onSuccess: (_d, { invid }) => invalidateInvoices(qc, invid),
  });
};

export const useRefundInvoiceMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { invid: number; body?: PaymentRefundPayload; idempotencyKey?: string }) => {
      return unwrap(request<any>(`${B}/invoices/${args.invid}/refund`, 'POST', args.body ?? {}, {
        ...TIMEOUT,
        idempotencyKey: args.idempotencyKey || generateIdempotencyKey(),
      }));
    },
    'Failed to refund invoice',
  );
  return useMutation({
    mutationFn: (args: { invid: number; body?: PaymentRefundPayload; idempotencyKey?: string }) =>
      fn(args),
    onSuccess: (_d, { invid }) => {
      invalidateInvoices(qc, invid);
      invalidatePayments(qc);
    },
  });
};

export const useUpdateInvoiceMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { invid: number; patch: Record<string, unknown> }) => {
    return unwrap(request<any>(`${B}/invoices/${args.invid}`, 'PATCH', args.patch, TIMEOUT));
  }, 'Failed to update invoice');
  return useMutation({
    mutationFn: (args: { invid: number; patch: Record<string, unknown> }) => fn(args),
    onSuccess: (_d, { invid }) => invalidateInvoices(qc, invid),
  });
};

// ─── Transactions ─────────────────────────────────────────────────────────────

export const useSetTransactionFulfilledMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { tid: number; fulfilled?: 0 | 1 }) => {
    return unwrap(request<any>(
      `${B}/transactions/${args.tid}/fulfilled`,
      'POST',
      { fulfilled: args.fulfilled ?? 1 },
      TIMEOUT,
    ));
  }, 'Failed to update transaction fulfilled flag');
  return useMutation({
    mutationFn: (args: { tid: number; fulfilled?: 0 | 1 }) => fn(args),
    onSuccess: (_d, { tid }) => invalidateTransactions(qc, tid),
  });
};

// ─── Subscriptions ────────────────────────────────────────────────────────────

export const useSubscriptionActionMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: {
      plid: number;
      action: 'cancel' | 'uncancel' | 'pause' | 'resume' | 'renew';
      body?: object;
    }) => {
      return unwrap(request<any>(
        `${B}/subscriptions/${args.plid}/${args.action}`,
        'POST',
        args.body ?? {},
        TIMEOUT,
      ));
    },
    'Failed to update subscription',
  );
  return useMutation({
    mutationFn: (args: {
      plid: number;
      action: 'cancel' | 'uncancel' | 'pause' | 'resume' | 'renew';
      body?: object;
    }) => fn(args),
    onSuccess: (_d, { plid }) => invalidateSubs(qc, plid),
  });
};

export const useUpdateSubscriptionMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { plid: number; patch: Partial<Subscription> }) => {
    return unwrap(request<any>(`${B}/subscriptions/${args.plid}`, 'PATCH', args.patch, TIMEOUT));
  }, 'Failed to edit subscription');
  return useMutation({
    mutationFn: (args: { plid: number; patch: Partial<Subscription> }) => fn(args),
    onSuccess: (_d, { plid }) => invalidateSubs(qc, plid),
  });
};

// ─── Wallets ──────────────────────────────────────────────────────────────────

export const useAdjustWalletMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { body: WalletAdjustPayload; idempotencyKey?: string }) => {
      return unwrap(request<any>(`${B}/wallets/adjust`, 'POST', args.body, {
        ...TIMEOUT,
        idempotencyKey: args.idempotencyKey || generateIdempotencyKey(),
      }));
    },
    'Failed to adjust wallet',
  );
  return useMutation({
    mutationFn: (args: { body: WalletAdjustPayload; idempotencyKey?: string }) => fn(args),
    onSuccess: () => qc.invalidateQueries({ queryKey: paymentKeys.wallets() }),
  });
};

/** complete | fail (needs a reason; releases the hold) | retry an owner withdrawal. */
export const useWalletPayoutActionMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  type Args = { poid: number; action: WalletPayoutAction; reason?: string };
  const fn = withErrorHandling(async ({ poid, action, reason }: Args) => {
    return unwrap(request<any>(`${B}/wallets/payouts/${poid}/${action}`, 'POST', { reason }, {
      ...TIMEOUT,
      idempotencyKey: generateIdempotencyKey(),
    }));
  }, 'Failed to update withdrawal');
  return useMutation({
    mutationFn: (args: Args) => fn(args),
    onSuccess: () => qc.invalidateQueries({ queryKey: paymentKeys.wallets() }),
  });
};

export const useFreezeWalletMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (wid: number) => {
    return unwrap(request<any>(`${B}/wallets/${wid}/freeze`, 'POST', {}, TIMEOUT));
  }, 'Failed to freeze wallet');
  return useMutation({
    mutationFn: (wid: number) => fn(wid),
    onSuccess: () => qc.invalidateQueries({ queryKey: paymentKeys.wallets() }),
  });
};

export const useUnfreezeWalletMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (wid: number) => {
    return unwrap(request<any>(`${B}/wallets/${wid}/unfreeze`, 'POST', {}, TIMEOUT));
  }, 'Failed to unfreeze wallet');
  return useMutation({
    mutationFn: (wid: number) => fn(wid),
    onSuccess: () => qc.invalidateQueries({ queryKey: paymentKeys.wallets() }),
  });
};

// ─── Orders ───────────────────────────────────────────────────────────────────

export const useSetOrderStatusMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { orderid: number; status: string; tracking_number?: string }) => {
      const { orderid, ...body } = args;
      return unwrap(request<any>(`${B}/orders/${orderid}/status`, 'POST', body, TIMEOUT));
    },
    'Failed to update order status',
  );
  return useMutation({
    mutationFn: (args: { orderid: number; status: string; tracking_number?: string }) => fn(args),
    onSuccess: (_d, { orderid }) => invalidateOrders(qc, orderid),
  });
};

export const useCancelOrderMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (orderid: number) => {
    return unwrap(request<any>(`${B}/orders/${orderid}/cancel`, 'POST', {}, TIMEOUT));
  }, 'Failed to cancel order');
  return useMutation({
    mutationFn: (orderid: number) => fn(orderid),
    onSuccess: (_d, orderid) => invalidateOrders(qc, orderid),
  });
};

export const useResolveRefundRequestMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: {
      rrid: number;
      action: RefundRequestAction;
      notes?: string;
      gateway?: boolean;
      idempotencyKey?: string;
    }) => {
      const body: Record<string, unknown> = {};
      if (args.notes) body.notes = args.notes;
      if (args.gateway != null) body.gateway = args.gateway;
      return unwrap(request<any>(`${B}/orders/refund-requests/${args.rrid}/${args.action}`, 'POST', body, {
        ...TIMEOUT,
        idempotencyKey: args.idempotencyKey || generateIdempotencyKey(),
      }));
    },
    'Failed to resolve refund request',
  );
  return useMutation({
    mutationFn: (args: {
      rrid: number;
      action: RefundRequestAction;
      notes?: string;
      gateway?: boolean;
      idempotencyKey?: string;
    }) => fn(args),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: paymentKeys.orders() });
      invalidatePayments(qc);
    },
  });
};

// ─── Payment methods ──────────────────────────────────────────────────────────

export const useDeletePaymentMethodMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (bpmid: number) => {
    return unwrap(request<any>(`${B}/payment-methods/${bpmid}`, 'DELETE', {}, TIMEOUT));
  }, 'Failed to delete payment method');
  return useMutation({
    mutationFn: (bpmid: number) => fn(bpmid),
    onSuccess: () => qc.invalidateQueries({ queryKey: paymentKeys.cards() }),
  });
};

export const useSetDefaultPaymentMethodMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (bpmid: number) => {
    return unwrap(request<any>(`${B}/payment-methods/${bpmid}/default`, 'PUT', {}, TIMEOUT));
  }, 'Failed to set default card');
  return useMutation({
    mutationFn: (bpmid: number) => fn(bpmid),
    onSuccess: () => qc.invalidateQueries({ queryKey: paymentKeys.cards() }),
  });
};
